import _ from 'underscore';
import { helpers as urlHelpers } from '@bingads-webui/notification-service';
import {
  contextKeys, severityOrder, whatsNewId, whatsNewSeverityOrder,
  actionCallbackTypes,
} from './constants';

export function isApexDisabled() {
  const uri = new URL(window.location.href);
  return uri.searchParams.get('apexDisabled') === 'true';
}

export const trimEndSlash = s => (_.isString(s) ? s.replace(/\/*$/, '') : s);

const getHelpPanel = () => document.querySelector('#apexHelpContainer');

const isTriggerFromHelpPanel = (target) => {
  const helpPanel = getHelpPanel();
  return helpPanel && helpPanel.contains(target);
};

export const customKeepOpen = ({ target }) => target.classList.contains('notification-queue-item-dismiss') ||
  (target.parentElement && target.parentElement.classList.contains('notification-queue-footer')) ||
  target.classList.contains('notification-queue-item-action-dismiss') ||
  isTriggerFromHelpPanel(target);

export const isContextChanged = (
  {
    cid: cid1, aid: aid1, uid: uid1, campaignId: campaignId1, pageEnum: pageEnum1,
  } = {},
  {
    cid: cid2, aid: aid2, uid: uid2, campaignId: campaignId2, pageEnum: pageEnum2,
  } = {}
) => (
  cid1 !== cid2 ||
  aid1 !== aid2 ||
  uid1 !== uid2 ||
  campaignId1 !== campaignId2 ||
  pageEnum1 !== pageEnum2
);

export const validateContext = ({ cid, aid, campaignId } = {}) => !!cid && (!campaignId || !!aid);

export const generateViewAllLinkInOldUI = campaignDomain => (
  { context: { cid, uid }, isOnAccountsSummaryPage },
  { accountNumber }
) => {
  const prefix = `${trimEndSlash(campaignDomain)}/Campaign/AccountsSummary?cid=${cid}&uid=${uid}`;
  const MCCHash = `#customer/${cid}/accounts-summary/notification`;
  const MCCHref = `${isOnAccountsSummaryPage ? '' : prefix}${MCCHash}`;
  const preFilter = accountNumber ? JSON.stringify([{
    Field: 'AccountNumber',
    Operator: 2,
    Values: accountNumber,
  }]) : 'all';

  return { href: `${MCCHref}/${preFilter}` };
};

export const getContextByParameters = parameters =>
  _.chain(parameters)
    .pick(contextKeys) // pick context keys
    .omit(val => _.isBoolean(val)) // omit boolean, Number(true) === 1
    .mapObject(Number) // convert to Number
    .pick((val, key) => val === parseInt(val, 10) && (val > 0 || key === 'pageEnum')) // pick positive integer
    .value();

export const getContext = ({ parameters, uid } = {}) =>
  getContextByParameters({
    uid,
    ...urlHelpers.getParameters(),
    ...(!_.isEmpty(parameters) && parameters),
  });

// log the errors as trace logs for now, needs to investigate what the [Object, Object] log is
export const logError = ({
  scenarioName,
  error,
  logContent,
  logger,
}) => {
  let mes = 'Caught unknown error';

  if (error) {
    mes = error.toString();

    if (!(error instanceof Error) && _.isObject(error)) {
      mes = error.message || JSON.stringify(error);
    }
  }

  logger.trace(scenarioName, logContent(mes));
};

export const sortNotifications = notifications => _.sortBy(notifications, ({ id, severity }) =>
  (id === whatsNewId ? whatsNewSeverityOrder : severityOrder[severity]));

export const getPreviewNotifications = (orderedNotifications) => {
  if (orderedNotifications.length === 0) {
    return [];
  }

  const previewNotifications = [orderedNotifications[0]];

  if (orderedNotifications.length === 1 ||
    severityOrder[orderedNotifications[0].severity] >= severityOrder.Information ||
    severityOrder[orderedNotifications[0].severity]
    === severityOrder[orderedNotifications[orderedNotifications.length - 1].severity]) {
    return previewNotifications;
  }

  for (let i = 1; i < orderedNotifications.length; i += 1) {
    if (severityOrder[orderedNotifications[i].severity]
      > severityOrder[orderedNotifications[0].severity]) {
      previewNotifications.push(orderedNotifications[i]);
      break;
    }
  }

  return previewNotifications;
};

export const getPreviewItems = previewNotifications => _.map(previewNotifications, ({ severity, title }) => ({ severity, content: title }));

export const wrapActionForUINext = (action, getPathBySiteMapId, goTargetPage) => {
  const res = action;
  const { siteMapId, search } = action;
  if (siteMapId) {
    const pathname = getPathBySiteMapId(siteMapId);

    if (pathname) {
      res.href = `${pathname}${search}`;
      res.isJumpOut = false;
      res.callbackType = actionCallbackTypes.closeQueue;
      res.action = (e) => {
        e.preventDefault(); // avoid opening href link during client-side navigation
        goTargetPage(siteMapId, search, res.href);
      };
    }
  }

  return res;
};
